import React from 'react';
import { FormControl, TextField, MenuItem, Box } from '@mui/material';
import RegularButton from 'components/CustomButtons/Button';
import { AutocompleteComponent } from 'components/Autocomplete/AutocompleteComponent';
import { useForm } from 'hooks/useForm';
import { useDispatch, useSelector } from 'react-redux';
import { createIotDevice, editIotDevice } from 'redux/actions/iotDevices/iotDevicesActions';

const STATUS_OPTIONS = ['ACTIVE', 'INACTIVE', 'MAINTENANCE', 'RETIRED'];
const MEASUREMENT_TYPES = ['NIVEL_GAS', 'PRESION', 'TEMPERATURA'];

const RULES = {
  idStation: { required: true },
  iotIdentity: { required: true },
  provider: { required: true },
  brand: { required: true },
  model: { required: true },
  measurementType: { required: true },
  status: { required: true },
  installationDate: { required: true },
};

export const IotDeviceForm = ({ onClose }) => {
  const dispatch = useDispatch();
  const { iotDeviceSelected, addMode, station } = useSelector(({ iotDevices }) => iotDevices);
  const { stationsList } = useSelector(({ companies }) => companies);
  const { isLoadingComp } = useSelector(({ search }) => search);

  const INITIAL = {
    idStation: addMode
      ? (station?.id ? station : '')
      : (stationsList.find(s => s.id === iotDeviceSelected?.idStation) ?? ''),
    iotIdentity: iotDeviceSelected?.iotIdentity ?? '',
    provider: iotDeviceSelected?.provider ?? '',
    brand: iotDeviceSelected?.brand ?? '',
    model: iotDeviceSelected?.model ?? '',
    measurementType: iotDeviceSelected?.measurementType ?? '',
    status: iotDeviceSelected?.status ?? 'ACTIVE',
    installationDate: iotDeviceSelected?.installationDate ?? '',
    lastCommunication: iotDeviceSelected?.lastCommunication ?? '',
    observations: iotDeviceSelected?.observations ?? '',
  };

  const { values, onChange, validate, errors, setValues } = useForm(INITIAL, RULES);

  const handleUpperCase = (e) => {
    e.target.value = e.target.value.toUpperCase();
    onChange(e);
  };

  const handleSubmit = () => {
    validate(addMode ? ['lastCommunication', 'observations'] : ['observations'], () => {
      const { lastCommunication: _, ...rest } = values;
      const form = {
        ...rest,
        idStation: values.idStation?.id,
        ...(addMode && { lastCommunication: values.lastCommunication || null }),
      };
      if (addMode) {
        dispatch(createIotDevice(form, onClose));
      } else {
        dispatch(editIotDevice(iotDeviceSelected.id, form, onClose));
      }
    });
  };

  return (
    <FormControl fullWidth style={{ display: 'flex', gap: 12, marginTop: 16 }}>
      <AutocompleteComponent
        name="idStation"
        value={values.idStation}
        options={stationsList}
        label="Planta *"
        handleValues={setValues}
        textFieldProps={{ error: !!errors.idStation, helperText: errors.idStation }}
        disabled={!!station?.id || !addMode}
      />
      <TextField
        label="Identidad IoT *"
        name="iotIdentity"
        value={values.iotIdentity}
        onChange={onChange}
        variant="outlined"
        error={!!errors.iotIdentity}
        helperText={errors.iotIdentity}
        disabled={false}
      />
      <TextField
        label="Proveedor *"
        name="provider"
        value={values.provider}
        onChange={handleUpperCase}
        variant="outlined"
        error={!!errors.provider}
        helperText={errors.provider}
      />
      <TextField
        label="Marca *"
        name="brand"
        value={values.brand}
        onChange={handleUpperCase}
        variant="outlined"
        error={!!errors.brand}
        helperText={errors.brand}
      />
      <TextField
        label="Modelo *"
        name="model"
        value={values.model}
        onChange={handleUpperCase}
        variant="outlined"
        error={!!errors.model}
        helperText={errors.model}
      />
      <TextField
        select
        label="Tipo de medición *"
        name="measurementType"
        value={values.measurementType}
        onChange={onChange}
        variant="outlined"
        error={!!errors.measurementType}
        helperText={errors.measurementType}
      >
        {MEASUREMENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
      </TextField>
      <TextField
        select
        label="Estatus *"
        name="status"
        value={values.status}
        onChange={onChange}
        variant="outlined"
      >
        {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
      </TextField>
      <TextField
        label="Fecha de instalación *"
        name="installationDate"
        type="date"
        value={values.installationDate}
        onChange={onChange}
        variant="outlined"
        InputLabelProps={{ shrink: true }}
        error={!!errors.installationDate}
        helperText={errors.installationDate}
      />
      {!addMode && values.lastCommunication && (
        <TextField
          label="Última comunicación"
          name="lastCommunication"
          value={values.lastCommunication}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          disabled
        />
      )}
      <TextField
        label="Observaciones"
        name="observations"
        value={values.observations}
        onChange={onChange}
        variant="outlined"
        multiline
        rows={3}
      />
      <Box display="flex" justifyContent="space-between" mt={2}>
        <RegularButton color="danger" onClick={onClose}>Cancelar</RegularButton>
        <RegularButton
          color="info"
          onClick={handleSubmit}
          disabled={isLoadingComp?.loadingIotDevices}
        >
          {addMode ? 'Crear' : 'Guardar'}
        </RegularButton>
      </Box>
    </FormControl>
  );
};
